import { type ReactNode } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import type { AuthenticatedSession } from '../../api'

/**
 * The organization id every administrative page works against, taken from the
 * URL rather than from a "current organization" held in memory.
 *
 * In the URL because these pages are worth sending to a colleague, and because
 * a person can administer more than one organization — a value held in state
 * would make two tabs disagree about which one they were looking at, on pages
 * whose actions are irreversible.
 */
export function useAdminOrg(session: AuthenticatedSession) {
  const { orgId = '' } = useParams()
  const membership = session.memberships.find((m) => m.organization_id === orgId)
  const administered = session.memberships.filter((m) => m.role === 'admin')
  return { orgId, membership, administered }
}

/**
 * The frame the four administrative pages share: the title, the organization
 * they are about, and the refusal when the person is not an administrator of
 * it.
 *
 * The refusal is rendered here as well as enforced in the store, which is not
 * duplication of the check — it is the difference between a page that refuses
 * politely and a page that shows a broken table full of 403s. The store's
 * answer is still the authority; nothing here is trusted to keep anyone out.
 */
export function AdminPage({
  session,
  title,
  intro,
  children,
}: {
  session: AuthenticatedSession
  title: string
  intro: string
  children: (orgId: string) => ReactNode
}) {
  const { orgId, membership, administered } = useAdminOrg(session)

  // No id in the path, or one this person does not belong to: send them to an
  // organization they do administer rather than showing an empty shell.
  if (!membership) {
    if (administered.length > 0) {
      return <Navigate to={`/orgs/${administered[0].organization_id}/settings`} replace />
    }
    return (
      <main className="page page-narrow">
        <div className="card empty">
          <h2>Nothing to administer</h2>
          <p className="muted">You are not an administrator of any organization.</p>
        </div>
      </main>
    )
  }

  if (membership.role !== 'admin') {
    return (
      <main className="page page-narrow">
        <div className="card empty">
          <h2>Only an administrator can do that</h2>
          <p className="muted">
            You are a member of {membership.organization_name}, not an administrator of it. Ask one
            of its administrators.
          </p>
        </div>
      </main>
    )
  }

  return (
    <main className="page">
      <div className="page-head">
        <div className="eyebrow">{membership.organization_name}</div>
        <h1>{title}</h1>
        <p>{intro}</p>
      </div>
      {children(orgId)}
    </main>
  )
}
